import { useCallback, useMemo } from 'react';
import type { FeatureKey, PermissionCode } from '@stockpilot/shared';
import { useAppSelector } from '@/app/hooks';
import { formatContextFrom, type FormatContext } from '@/lib/format';

/**
 * Permission and feature checks for the UI.
 *
 * These only decide what to RENDER. Every one of them is enforced again by the
 * API — hiding a button is a courtesy, not a security boundary.
 */
export function useAccess() {
  const context = useAppSelector((state) => state.tenant.context);

  const permissions = useMemo(
    () => new Set<string>(context?.permissions ?? []),
    [context?.permissions],
  );
  const features = useMemo(
    () => new Set<string>(context?.features ?? []),
    [context?.features],
  );

  const can = useCallback(
    (...required: PermissionCode[]) => required.every((code) => permissions.has(code)),
    [permissions],
  );

  const canAny = useCallback(
    (...required: PermissionCode[]) => required.some((code) => permissions.has(code)),
    [permissions],
  );

  const hasFeature = useCallback(
    (feature: FeatureKey) => features.has(feature),
    [features],
  );

  return {
    can,
    canAny,
    hasFeature,
    isOwner: context?.membership.isOwner ?? false,
    permissions,
    features,
    ready: context !== null,
  };
}

/** Currency, date and number formatting driven by the business's settings. */
export function useFormat(): FormatContext {
  const settings = useAppSelector((state) => state.tenant.context?.settings);
  return useMemo(() => formatContextFrom(settings), [settings]);
}

export function useActiveBranch() {
  const branchId = useAppSelector((state) => state.tenant.branchId);
  const branches = useAppSelector((state) => state.tenant.context?.branches ?? []);
  return useMemo(
    () => branches.find((branch) => branch.id === branchId) ?? branches[0] ?? null,
    [branches, branchId],
  );
}
